import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { getEventCategory } from '../../theme/categories';
import { getEventState } from '../../utils/eventState';

// Pricing now lives with the rest of the event-state derivation. Re-exported
// because SimilarEventsRail and OrganizerProfilePage import it from here.
export { getEventEffectivePrice } from '../../utils/eventState';

/**
 * EventCard — Vibana's signature editorial event poster.
 *
 * An immersive, full-bleed poster: cover art with a Fraunces date stamp,
 * a category kicker, a serif title and price over a warm scrim. Distinct
 * from a generic image-plus-text card — every event reads like a bill poster.
 *
 * Props: event, isSaved, onSaveToggle, aspectRatio (default portrait 4/5).
 * Memoized — this is the dominant repeated component in event lists.
 */
function EventCard({
  event,
  isSaved,
  onSaveToggle,
  aspectRatio = '4 / 5',
}) {
  const { VB } = useTheme();
  const date = new Date(event.date);
  const dayNum = date.getDate();
  const monthAbbr = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

  // One derivation for price, availability and lifecycle — a sold-out or past
  // event used to render here exactly like an available one.
  const state = getEventState(event);
  const isTonight = state.isToday && !state.isPast;
  const price = state.priceLabel;
  const dimmed = state.isPast || state.isSoldOut;

  const showSaveButton = typeof onSaveToggle === 'function';
  const cat = getEventCategory(event.category);
  const city = event.venue?.city ? event.venue.city.toUpperCase() : '';
  const kicker = isTonight
    ? ['TONIGHT', city].filter(Boolean).join(' · ')
    : [(event.category || 'EVENT').toUpperCase(), city].filter(Boolean).join(' · ');

  // Exactly one badge, resolved by precedence in getEventState — a card should
  // never stack "Tonight" on top of "Sold out".
  const BADGES = {
    ended:    { label: 'Ended',    bg: 'rgba(10,9,8,0.62)', color: 'rgba(251,244,230,0.72)', border: '1px solid rgba(251,244,230,0.18)' },
    soldout:  { label: 'Sold out', bg: 'rgba(10,9,8,0.62)', color: '#ff8f88',                border: `1px solid ${VB.red}66` },
    tonight:  { label: 'Tonight',  bg: VB.red,              color: '#fff',                   shadow: '0 4px 14px rgba(230,57,70,0.4)' },
    lowstock: { label: state.remaining === 1 ? '1 left' : `${state.remaining} left`, bg: 'rgba(10,9,8,0.5)', color: VB.gold, border: '1px solid rgba(233,162,59,0.35)' },
    featured: { label: 'Featured', bg: 'rgba(10,9,8,0.5)',  color: VB.gold,                  border: '1px solid rgba(233,162,59,0.35)' },
  };
  const badge = state.status ? BADGES[state.status] : null;

  return (
    <div style={{ position: 'relative', display: 'block' }}>
      <Link to={'/events/' + (event.slug || event._id)} style={{ display: 'block', textDecoration: 'none' }}>
        <div
          className="vb-ecard"
          style={{
            borderRadius: 20,
            overflow: 'hidden',
            position: 'relative',
            aspectRatio,
            cursor: 'pointer',
            background: VB.darkCardAlt,
          }}
        >
          {/* Cover art. Sold-out and finished events desaturate so an unavailable
              card is distinguishable at a glance in a dense grid, not only by
              reading its badge. */}
          <div
            className="vb-ecard__media"
            style={{
              position: 'absolute', inset: 0,
              filter: dimmed ? 'saturate(0.35) brightness(0.72)' : 'none',
              transition: 'filter 360ms ease',
            }}
          >
            {event.coverImage ? (
              <img
                className="vb-ecard__img"
                src={event.coverImage}
                alt={`${event.title} — ${event.category || 'Event'} in ${event.venue?.city || 'your city'}`}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{
                position: 'absolute', inset: 0,
                background: `radial-gradient(120% 120% at 22% 14%, ${cat.color}44, transparent 56%), linear-gradient(150deg, ${VB.darkCardAlt} 0%, ${VB.void} 100%)`,
              }} />
            )}
            <div className="vb-ecard__poster-scrim" />
          </div>

          {/* Top-left cluster — date stamp + status (save button floats top-right) */}
          <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'flex-start', gap: 7, zIndex: 2 }}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1,
              background: 'rgba(10,9,8,0.5)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(217,168,59,0.22)', borderRadius: 13, padding: '8px 11px 7px', minWidth: 46,
            }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: VB.gold }}>{weekday}</span>
              <span style={{ fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', fontSize: 23, fontWeight: 600, color: '#fff', letterSpacing: '-0.02em', margin: '1px 0' }}>{dayNum}</span>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(251,244,230,0.72)' }}>{monthAbbr}</span>
            </div>
            {badge ? (
              <span style={{
                background: badge.bg, color: badge.color, border: badge.border,
                boxShadow: badge.shadow, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                padding: '5px 9px', borderRadius: 7, whiteSpace: 'nowrap',
              }}>
                {badge.label}
              </span>
            ) : null}
          </div>

          {/* Bottom overlay — kicker, title, meta */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '16px 16px 17px', zIndex: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
              <p style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: isTonight ? '#ff7a72' : VB.gold, margin: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {kicker}
              </p>
              {event.organizer?.isVerifiedOrganizer && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={VB.gold} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-label="Verified organizer"><polyline points="20 6 9 17 4 12"/></svg>
              )}
              {/* Founding organizer. A filled star beside the outlined check so
                  the two read as different things at a glance rather than as one
                  louder tick. */}
              {event.organizer?.isFoundingOrganizer && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill={VB.gold} stroke="none" style={{ flexShrink: 0 }} aria-label="Founding organizer"><path d="M12 2.6l2.9 5.88 6.49.95-4.7 4.58 1.11 6.46L12 17.42l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.95L12 2.6z"/></svg>
              )}
            </div>
            <h3 style={{
              fontSize: 21, fontWeight: 500, color: '#fff', lineHeight: 1.12, margin: 0,
              letterSpacing: '-0.02em', fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              textShadow: '0 1px 20px rgba(0,0,0,0.4)',
            }}>
              {event.title}
            </h3>
            <div className="vb-ecard__underline" style={{ margin: '11px 0 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{
                fontSize: 12.5, color: 'rgba(251,244,230,0.74)', margin: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
              }}>
                {event.venue?.name && event.venue.name !== 'TBD' ? event.venue.name : 'Venue TBA'}
              </span>
              <span style={{
                flexShrink: 0, fontSize: 12, fontWeight: 800, letterSpacing: '0.02em',
                color: dimmed ? 'rgba(251,244,230,0.72)' : (state.isFree ? '#0d1512' : '#1a1613'),
                background: dimmed ? 'rgba(251,244,230,0.10)' : (state.isFree ? VB.teal : VB.ivory),
                border: dimmed ? '1px solid rgba(251,244,230,0.18)' : 'none',
                padding: '5px 11px', borderRadius: 999, fontVariantNumeric: 'tabular-nums',
                // Struck through only when it sold out — the price was real and
                // is no longer purchasable. A finished event just goes quiet.
                textDecoration: state.isSoldOut ? 'line-through' : 'none',
              }}>
                {price}
              </span>
            </div>
          </div>
        </div>
      </Link>

      {showSaveButton ? (
        <button
          type="button"
          onClick={e => { e.preventDefault(); e.stopPropagation(); onSaveToggle(event._id); }}
          title={isSaved ? 'Remove from saved events' : 'Save event'}
          aria-label={isSaved ? 'Remove from saved events' : 'Save event'}
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 36, height: 36, borderRadius: '50%',
            border: `1px solid ${isSaved ? VB.red : 'rgba(217,168,59,0.26)'}`,
            background: isSaved ? `${VB.red}2e` : 'rgba(10,9,8,0.5)',
            backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            color: isSaved ? '#ff7a72' : '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, padding: 0,
            transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2"
            style={{ transition: 'transform 260ms cubic-bezier(0.34,1.56,0.64,1)', transform: isSaved ? 'scale(1.2)' : 'scale(1)' }}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
      ) : null}
    </div>
  );
}

export default React.memo(EventCard, (prev, next) => (
  prev.event?._id === next.event?._id &&
  prev.event?.updatedAt === next.event?.updatedAt &&
  prev.isSaved === next.isSaved &&
  prev.onSaveToggle === next.onSaveToggle
));
